import {
  CSRF_HEADER,
  changeRequestState,
  changeSizes,
  type ChangesInbox as Inbox,
  type ProjectDetail,
  type ReviewGroup,
  type ReviewSetRow,
  type WikiClosedChangeSet,
  type WikiReviewItem,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CircleCheck,
  CircleDot,
  Eye,
  History,
  Inbox as InboxIcon,
  MessageSquareWarning,
  Search,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useDeferredValue, useState } from "react";
import { EmptyState } from "@/components/EmptyState";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { noContent } from "../api-errors.ts";
import { formatAgo } from "../i18n/format.ts";
import { t } from "../i18n/i18n.ts";
import {
  changeSetPath,
  INBOX_VIEWS,
  type InboxView,
  Link,
  wikiChangesPath,
  wikiPath,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { useChangesInbox, useClosedChanges, wikiKeys } from "./queries.ts";
import { sizesText } from "./sizes.ts";

const VIEW_KEYS: Record<InboxView, "toDecide" | "mine" | "following" | "closed"> = {
  "to-decide": "toDecide",
  mine: "mine",
  following: "following",
  closed: "closed",
};

/**
 * The wiki's Changes inbox (wiki-collaboration-ux D4, D5; spec `project-wiki` "Review queue
 * groups rows by who acts"): To decide (the only view counted), Mine, Following and Closed, one
 * per URL (`?tab=changes&view=`). Rows read the same predicate as the count; Closed pages through
 * published and discarded sets with a title search.
 */
export function ChangesInbox({ project, view }: { project: ProjectDetail; view: InboxView }) {
  const { queue, inbox } = useChangesInbox(project, true, true);
  const counts: Partial<Record<InboxView, number>> = inbox
    ? { "to-decide": inbox.count, mine: inbox.mine.changeSets.length }
    : {};
  return (
    // Tenant content (titles, comments): masked in feedback screenshots, as the queue was.
    <div data-esi-content="wiki" className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-fg">{t("wiki.inbox.title")}</h2>
        <nav aria-label={t("wiki.inbox.views")} className="-mx-1 overflow-x-auto px-1 pb-0.5">
          <ul className="inline-flex h-9 items-center gap-0.5 rounded-md border border-line bg-surface-sunken p-0.5">
            {INBOX_VIEWS.map((v) => {
              const current = v === view;
              const count = counts[v];
              return (
                <li key={v} className="h-full">
                  <Link
                    href={wikiChangesPath(project.id, v)}
                    aria-current={current ? "page" : undefined}
                    className={cn(
                      "inline-flex h-full items-center gap-1.5 rounded-[5px] px-3 text-[13px] font-medium whitespace-nowrap text-fg-secondary outline-none transition-colors duration-(--duration-base) hover:text-fg focus-visible:ring-2 focus-visible:ring-ring",
                      current && "bg-surface-raised text-fg shadow-xs ring-1 ring-line",
                    )}
                  >
                    {t(`wiki.inbox.view.${VIEW_KEYS[v]}`)}
                    {count ? (
                      <span className="text-fg-muted tabular-nums">
                        {count}
                        <span className="sr-only"> {t("wiki.inbox.countSr", { count })}</span>
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
      {view === "closed" ? (
        <ClosedView projectId={project.id} />
      ) : queue.isError ? (
        <Notice tone="danger">{queue.error.message}</Notice>
      ) : !inbox ? (
        <RowsSkeleton />
      ) : (
        <OpenView project={project} view={view} inbox={inbox} />
      )}
    </div>
  );
}

function RowsSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <span className="sr-only">{t("wiki.inbox.loading")}</span>
      {[0, 1, 2].map((n) => (
        <Skeleton key={n} className="h-20 w-full" />
      ))}
    </div>
  );
}

function OpenView(props: {
  project: ProjectDetail;
  view: Exclude<InboxView, "closed">;
  inbox: Inbox;
}) {
  const { project, view, inbox } = props;
  const projectId = project.id;
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const resolve = useMutation({
    mutationFn: async (itemId: string) =>
      noContent(
        await api.api.projects[":projectId"].wiki.review[":itemId"].resolve.$post(
          { param: { projectId, itemId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(projectId) }),
  });
  const group: ReviewGroup =
    view === "to-decide" ? inbox.toDecide : view === "mine" ? inbox.mine : inbox.following;
  if (!group.changeSets.length && !group.items.length) {
    return (
      <EmptyState
        icon={view === "to-decide" ? CircleCheck : view === "mine" ? InboxIcon : Eye}
        title={t(`wiki.inbox.empty.${VIEW_KEYS[view]}.title`)}
        headingLevel={3}
        className="mt-4"
      >
        {t(`wiki.inbox.empty.${VIEW_KEYS[view]}.body`)}
      </EmptyState>
    );
  }
  const rows = [...group.changeSets].sort((a, b) => rowAt(b, view).localeCompare(rowAt(a, view)));
  return (
    <div className="flex flex-col gap-4">
      {resolve.error ? <Notice tone="danger">{resolve.error.message}</Notice> : null}
      {rows.length ? (
        <ul
          aria-label={t(`wiki.inbox.view.${VIEW_KEYS[view]}`)}
          className="divide-y divide-line rounded-xl border border-line bg-surface-raised"
        >
          {rows.map((row) => (
            <SetRow key={row.set.id} projectId={projectId} row={row} view={view} />
          ))}
        </ul>
      ) : null}
      {group.items.length ? (
        <section aria-labelledby="inbox-alerts" className="flex flex-col gap-2">
          <h3 id="inbox-alerts" className="text-sm font-medium text-fg">
            {t("wiki.inbox.alerts")}
          </h3>
          <ul
            aria-labelledby="inbox-alerts"
            className="divide-y divide-line rounded-xl border border-line bg-surface-raised"
          >
            {group.items.map((item) => (
              <AlertRow
                key={item.id}
                projectId={projectId}
                item={item}
                onResolve={
                  view === "to-decide" && inbox.viewer.canResolve(item.kind)
                    ? () => resolve.mutate(item.id)
                    : undefined
                }
                resolving={resolve.isPending && resolve.variables === item.id}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** When a row last moved, newest first: submitted, else last updated. */
const rowAt = ({ set }: ReviewSetRow, view: InboxView) =>
  (view === "mine" ? set.updatedAt : (set.submittedAt ?? set.updatedAt)) ?? "";

/** "Agent for Demo Contributor", "Demo Owner", "Saved answer · Demo Contributor". */
function whoOf(origin: string, name: string | null): string {
  const who = name ?? t("wiki.inbox.someone");
  switch (origin) {
    case "agent":
      return t("wiki.inbox.who.agent", { name: who });
    case "answer":
      return t("wiki.inbox.who.answer", { name: who });
    case "mcp":
      return t("wiki.inbox.who.mcp", { name: who });
    case "revert":
      return t("wiki.inbox.who.revert", { name: who });
    case "ingestion":
      return t("wiki.inbox.who.ingestion");
    default:
      return who;
  }
}

/** One change set of an open view: title, sizes, risks inline, who, when and why. */
function SetRow(props: { projectId: string; row: ReviewSetRow; view: InboxView }) {
  const { projectId, row, view } = props;
  const { set, flags, waiting, reason } = row;
  const title = (!set.hiddenItems && set.title) || t("wiki.changes.hiddenTitle");
  const working = set.submittedAt === null;
  const stale = set.items.filter((i) => i.status === "stale").length;
  const requested = (set.openThreads ?? []).filter((th) => th.kind === "change_request").length;
  const risks = [
    ...(flags.length ? [t("wiki.review.contradictions", { count: flags.length })] : []),
    ...(waiting?.changeRequests
      ? [t("wiki.review.waitingChangeRequests", { count: waiting.changeRequests })]
      : requested
        ? [t("wiki.inbox.risk.changeRequests", { count: requested })]
        : []),
    ...(waiting?.questions
      ? [t("wiki.review.waitingQuestions", { count: waiting.questions })]
      : []),
    ...(stale ? [t("wiki.inbox.risk.stale", { count: stale })] : []),
    ...(set.hiddenItems ? [t("wiki.inbox.risk.hidden")] : []),
  ];
  const blocked = changeRequestState(set).publishBlocked;
  const who = whoOf(set.origin, set.provenance.createdByName);
  const when =
    set.status === "changes_requested" && set.reviewCommentAt
      ? set.reviewCommentByName
        ? t("wiki.inbox.when.sentBackBy", {
            name: set.reviewCommentByName,
            ago: formatAgo(set.reviewCommentAt),
          })
        : t("wiki.inbox.when.sentBack", { ago: formatAgo(set.reviewCommentAt) })
      : working
        ? t("wiki.inbox.when.started", { ago: formatAgo(set.createdAt) })
        : t("wiki.inbox.when.submitted", { ago: formatAgo(set.submittedAt ?? set.createdAt) });
  const state =
    view === "mine"
      ? working
        ? t("wiki.inbox.state.working")
        : set.status === "changes_requested"
          ? t("wiki.changes.state.changes_requested")
          : t("wiki.inbox.state.submitted")
      : set.status === "changes_requested"
        ? t("wiki.changes.state.changes_requested")
        : null;
  // "You may publish" only where the viewer decides (To decide); elsewhere the line names who does.
  const why =
    view === "mine"
      ? null
      : set.status === "changes_requested" && reason !== "waiting"
        ? t("wiki.inbox.why.author")
        : reason === "requester" && view !== "to-decide"
          ? t("wiki.inbox.why.publisher")
          : reason
            ? t(`wiki.inbox.why.${reason}`)
            : null;
  return (
    <li className="group relative flex items-start gap-4 px-4 py-3.5 transition-colors duration-(--duration-base) hover:bg-surface-hover sm:px-5">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link
          href={changeSetPath(projectId, set.id)}
          className="font-medium text-fg outline-none after:absolute after:inset-0 after:rounded-[inherit] focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset"
        >
          {title}
        </Link>
        <p className="text-[13px] text-fg-secondary tabular-nums">{sizesText(changeSizes(set))}</p>
        {risks.length ? (
          <p className="flex items-start gap-1.5 text-xs text-warning-fg">
            <RiskIcon blocked={blocked || !!waiting} />
            <span>{risks.join(" · ")}</span>
          </p>
        ) : null}
        <p className="text-xs text-fg-muted">{[who, when, ...(why ? [why] : [])].join(" · ")}</p>
      </div>
      {state ? (
        <span className="mt-0.5 shrink-0 text-xs font-medium whitespace-nowrap text-fg-secondary">
          {state}
        </span>
      ) : null}
    </li>
  );
}

function RiskIcon({ blocked }: { blocked: boolean }) {
  const Icon = blocked ? MessageSquareWarning : TriangleAlert;
  return <Icon aria-hidden="true" className="mt-px size-3.5 shrink-0" />;
}

/** A review item not attached to a change item (a source changed, a re-review). */
function AlertRow(props: {
  projectId: string;
  item: WikiReviewItem;
  onResolve: (() => void) | undefined;
  resolving: boolean;
}) {
  const { projectId, item, onResolve, resolving } = props;
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3.5 text-sm sm:px-5">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-medium text-fg">{t(`wiki.review.kind.${item.kind}`)}</span>
        <span className="text-[13px] text-fg-secondary">
          {item.page ? (
            <Link
              href={wikiPath(projectId, item.page.id)}
              className="text-accent-text hover:underline"
            >
              {item.page.title}
            </Link>
          ) : null}
          {item.source ? `${item.page ? " ← " : ""}${item.source.title}` : null}
        </span>
        {item.detail ? (
          <span className="text-xs text-fg-secondary" data-esi-content="wiki">
            {item.detail}
          </span>
        ) : null}
        <span className="text-xs text-fg-muted">{formatAgo(item.createdAt)}</span>
      </div>
      {onResolve ? (
        <Button size="sm" variant="outline" disabled={resolving} onClick={onResolve}>
          {t("wiki.review.resolve")}
        </Button>
      ) : null}
    </li>
  );
}

/** The Closed view: published and discarded sets, newest decision first, searched by title. */
function ClosedView({ projectId }: { projectId: string }) {
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query);
  const closed = useClosedChanges(projectId, deferred);
  const rows = closed.data?.pages.flatMap((page) => page.items) ?? [];
  const searching = deferred.trim() !== "";
  // A page can hold no set the viewer may see and still point to older ones (the server stops
  // after a few hidden batches): Show older stays reachable.
  const older = closed.hasNextPage ? (
    <Button
      variant="outline"
      size="sm"
      className="self-start"
      disabled={closed.isFetchingNextPage}
      onClick={() => void closed.fetchNextPage()}
    >
      {t("wiki.changes.older")}
    </Button>
  ) : null;
  let body: ReactNode;
  if (closed.isError && !rows.length) {
    body = <Notice tone="danger">{closed.error.message}</Notice>;
  } else if (!closed.data) {
    body = <RowsSkeleton />;
  } else if (!rows.length && older) {
    body = (
      <>
        <p className="text-sm text-fg-secondary">{t("wiki.inbox.empty.closed.recent")}</p>
        {older}
      </>
    );
  } else if (!rows.length) {
    body = searching ? (
      <p className="text-sm text-fg-secondary">
        {t("wiki.inbox.empty.closed.search", { query: deferred.trim() })}
      </p>
    ) : (
      <EmptyState
        icon={History}
        title={t("wiki.inbox.empty.closed.title")}
        headingLevel={3}
        className="mt-4"
      >
        {t("wiki.inbox.empty.closed.body")}
      </EmptyState>
    );
  } else {
    body = (
      <>
        <ul
          aria-label={t("wiki.inbox.view.closed")}
          className="divide-y divide-line rounded-xl border border-line bg-surface-raised"
        >
          {rows.map((row) => (
            <ClosedRow key={row.id} projectId={projectId} row={row} />
          ))}
        </ul>
        {closed.isError ? <Notice tone="danger">{closed.error.message}</Notice> : null}
        {older}
      </>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="relative w-full sm:max-w-xs">
        <label htmlFor={`closed-search-${projectId}`} className="sr-only">
          {t("wiki.inbox.search")}
        </label>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
        />
        <Input
          id={`closed-search-${projectId}`}
          type="search"
          autoComplete="off"
          maxLength={200}
          placeholder={t("wiki.inbox.search")}
          className="pl-8"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div aria-live="polite" aria-busy={closed.isFetching} className="flex flex-col gap-4">
        {body}
      </div>
    </div>
  );
}

/** One finished set: title, sizes of its readable items, who decided, when, the comment. */
function ClosedRow({ projectId, row }: { projectId: string; row: WikiClosedChangeSet }) {
  const published = row.status === "published";
  const at = formatAgo(row.decidedAt);
  const decided =
    row.decidedBy === null
      ? t("wiki.inbox.closed.swept", { at })
      : t(published ? "wiki.inbox.closed.published" : "wiki.inbox.closed.discarded", {
          name: row.decidedByName ?? t("wiki.inbox.someone"),
          at,
        });
  const made = whoOf(row.origin, row.createdByName);
  // The review when it opens for the viewer (absent `openable`: an N-1 API, as before); else the
  // first page version this set published, which they may read; else no link.
  const page = row.items.find((i) => i.publishedDocumentId && i.publishedVersion);
  const href =
    row.openable !== false
      ? changeSetPath(projectId, row.id)
      : page?.publishedDocumentId && page.publishedVersion
        ? wikiPath(projectId, page.publishedDocumentId, page.publishedVersion)
        : null;
  return (
    <li className="group relative flex items-start gap-4 px-4 py-3.5 transition-colors duration-(--duration-base) hover:bg-surface-hover sm:px-5">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {href ? (
          <Link
            href={href}
            className="font-medium text-fg outline-none after:absolute after:inset-0 after:rounded-[inherit] focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset"
          >
            {row.title || t("wiki.changes.hiddenTitle")}
          </Link>
        ) : (
          <span className="font-medium text-fg">{row.title || t("wiki.changes.hiddenTitle")}</span>
        )}
        <p className="text-[13px] text-fg-secondary tabular-nums">
          {sizesText(changeSizes({ items: row.items }))}
          {row.hiddenCount ? (
            <span className="text-fg-muted">
              {" · "}
              {t("wiki.inbox.closed.hidden", { count: row.hiddenCount })}
            </span>
          ) : null}
        </p>
        <p className="text-xs text-fg-muted">
          {decided} · {made}
        </p>
        {row.comment ? (
          <p className="text-xs text-fg-secondary">
            <span className="sr-only">{t("wiki.inbox.closed.comment")} </span>
            <span data-esi-content="wiki">“{row.comment}”</span>
          </p>
        ) : null}
      </div>
      <span
        className={cn(
          "mt-0.5 inline-flex shrink-0 items-center gap-1 text-xs font-medium whitespace-nowrap",
          published ? "text-fg-secondary" : "text-fg-muted",
        )}
      >
        <CircleDot
          aria-hidden="true"
          className={cn("size-3", published ? "text-success-fg" : "text-fg-muted")}
        />
        {t(`wiki.changes.state.${row.status}`)}
      </span>
    </li>
  );
}
